import _classCallCheck from "/builder/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/builder/node_modules/@babel/runtime/helpers/esm/createClass.js";
import _assertThisInitialized from "/builder/node_modules/@babel/runtime/helpers/esm/assertThisInitialized.js";
import _inherits from "/builder/node_modules/@babel/runtime/helpers/esm/inherits.js";
import _createSuper from "/builder/node_modules/@babel/runtime/helpers/esm/createSuper.js";
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { extension, ExtensionPriority, helper, PlainExtension, range, getMarkRange, isString, invariant, ErrorConstant, entries, noop } from '@remirror/core';
/**
 * A port of `view.posAtDOM` without throwing error when the dom node is not
 * inside the editor.
 */

function posAtDOM(view, node, offset, bias) {
  return view.docView.posFromDOM(node, offset, bias);
}
/**
 * Extract the position from a provided event.
 */


function getPositionFromEvent(view, event) {
  var _view$posAtCoords;

  var target = event.target;

  if (target) {
    var pos = posAtDOM(view, target, 0);

    if (pos !== null) {
      var $pos = view.state.doc.resolve(pos);
      var border = $pos.node().isLeaf ? 0 : 1; // https://github.com/ProseMirror/prosemirror-view/blob/9af8ceac1ee60a30ced3611913db7f187bbb51ed/src/domcoords.js#L283

      var inside = $pos.start() - border;
      return {
        pos: pos,
        inside: inside
      };
    }
  } // If the coordinates are just at the boundary of two elements, `view.posAtCoords`
  // can't distinguish which one should be returned. This is not a big deal for
  // `click` event but it's a huge problem for `mouseover` and `mouseout` events.
  // That's why we try to use `posAtDOM` firstly and only use `posAtCoords` if
  // `posAtDOM` doesn't work.


  return (_view$posAtCoords = view.posAtCoords({
    left: event.clientX,
    top: event.clientY
  })) !== null && _view$posAtCoords !== void 0 ? _view$posAtCoords : undefined;
}
/**
 * The position created by `view.posAtCoords`
 */


var _dec, _dec2, _class, _class2;

var EventsExtension = (_dec = extension({
  handlerKeys: ['blur', 'focus', 'mousedown', 'mouseup', 'mouseenter', 'mouseleave', 'textInput', 'keypress', 'keyup', 'keydown', 'click', 'clickMark', 'contextmenu', 'hover', 'scroll', 'copy', 'paste'],
  handlerKeyOptions: {
    blur: {
      earlyReturnValue: true
    },
    focus: {
      earlyReturnValue: true
    },
    mousedown: {
      earlyReturnValue: true
    },
    mouseleave: {
      earlyReturnValue: true
    },
    mouseup: {
      earlyReturnValue: true
    },
    click: {
      earlyReturnValue: true
    },
    hover: {
      earlyReturnValue: true
    },
    contextmenu: {
      earlyReturnValue: true
    },
    scroll: {
      earlyReturnValue: true
    },
    copy: {
      earlyReturnValue: true
    },
    paste: {
      earlyReturnValue: true
    }
  },
  defaultPriority: ExtensionPriority.High
}), _dec2 = helper(), _dec(_class = (_class2 = /*#__PURE__*/function (_PlainExtension) {
  _inherits(EventsExtension, _PlainExtension);

  var _super = _createSuper(EventsExtension);

  function EventsExtension() {
    var _this;

    _classCallCheck(this, EventsExtension);

    _this = _super.apply(this, arguments);

    _defineProperty(_assertThisInitialized(_this), "mousedown", false);

    _defineProperty(_assertThisInitialized(_this), "mouseover", false);

    _defineProperty(_assertThisInitialized(_this), "createMouseEventHandler", function (fn) {
      return function (view, event) {
        var eventPosition = getPositionFromEvent(view, event);

        if (!eventPosition) {
          return false;
        } // The nodes that are captured by the context menu. An empty array
        // means the contextmenu was trigger outside the content. The first
        // node is always the direct match.


        var nodes = []; // The marks wrapping the captured position.

        var marks = [];
        var inside = eventPosition.inside; // This handle the case when the context menu click has no corresponding
        // nodes or marks because it's outside of any editor content.

        if (inside === -1) {
          return false;
        } // Retrieve the resolved position from the current state.


        var $pos = view.state.doc.resolve(inside); // The depth of the current node (which is a direct match)

        var currentNodeDepth = $pos.depth + 1; // Populate the nodes.

        var _iterator = _createForOfIteratorHelper(range(currentNodeDepth, 1)),
            _step;

        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var index = _step.value;
            nodes.push({
              node: index > $pos.depth && $pos.nodeAfter ? $pos.nodeAfter : $pos.node(index),
              pos: $pos.before(index)
            });
          } // Populate the marks.

        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }

        var _iterator2 = _createForOfIteratorHelper($pos.marks()),
            _step2;

        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var type = _step2.value.type;

            var _range = getMarkRange($pos, type);

            if (_range) {
              marks.push(_range);
            }
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }

        return fn({
          event: event,
          view: view,
          nodes: nodes,
          marks: marks,
          getMark: function getMark(markType) {
            var type = isString(markType) ? view.state.schema.marks[markType] : markType;
            !type ? process.env.NODE_ENV !== "production" ? invariant(false, {
              code: ErrorConstant.EXTENSION,
              message: "The mark ".concat(markType, " being checked does not exist within the editor schema.")
            }) : invariant(false) : void 0;
            return marks.find(function (range) {
              return range.mark.type === type;
            });
          },
          getNode: function getNode(nodeType) {
            var _nodes$;

            var type = isString(nodeType) ? view.state.schema.nodes[nodeType] : nodeType;
            !type ? process.env.NODE_ENV !== "production" ? invariant(false, {
              code: ErrorConstant.EXTENSION,
              message: 'The node being checked does not exist'
            }) : invariant(false) : void 0;
            var nodeWithPos = nodes.find(function (_ref) {
              var node = _ref.node;
              return node.type === type;
            });

            if (!nodeWithPos) {
              return;
            }

            return _objectSpread(_objectSpread({}, nodeWithPos), {}, {
              isRoot: !!((_nodes$ = nodes[0]) !== null && _nodes$ !== void 0 && _nodes$.node.eq(nodeWithPos.node))
            });
          }
        });
      };
    });

    return _this;
  }

  _createClass(EventsExtension, [{
    key: "name",
    get: function get() {
      return 'events';
    }
    /**
     * Indicates whether the user is currently interacting with the editor.
     */

    /**
     * Add a new lifecycle method which is available to all extensions for adding
     * a click handler to the node or mark.
     */

  }, {
    key: "onView",
    value: function onView() {
      var _this$store$managerSe;

      if ( // managerSettings excluded this from running
      (_this$store$managerSe = this.store.managerSettings.exclude) !== null && _this$store$managerSe !== void 0 && _this$store$managerSe.clickHandler) {
        return;
      }

      var _iterator3 = _createForOfIteratorHelper(this.store.extensions),
          _step3;

      try {
        for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
          var _extension$options$ex;

          var _extension = _step3.value;

          if ( // Method doesn't exist
          !_extension.createEventHandlers || // Extension settings exclude it
          (_extension$options$ex = _extension.options.exclude) !== null && _extension$options$ex !== void 0 && _extension$options$ex.clickHandler) {
            continue;
          }

          var eventHandlers = _extension.createEventHandlers();

          var _iterator4 = _createForOfIteratorHelper(entries(eventHandlers)),
              _step4;

          try {
            for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
              var _step4$value = _slicedToArray(_step4.value, 2),
                  key = _step4$value[0],
                  handler = _step4$value[1]; // Casting to `any` needed here since I don't know how to teach
              // `TypeScript` that the object key and the handler are a valid pair.


              this.addHandler(key, handler);
            }
          } catch (err) {
            _iterator4.e(err);
          } finally {
            _iterator4.f();
          }
        }
      } catch (err) {
        _iterator3.e(err);
      } finally {
        _iterator3.f();
      }
    }
    /**
     * Create the plugin which manages all of the events being listened to within
     * the editor.
     */

  }, {
    key: "createPlugin",
    value: function createPlugin() {
      var _this2 = this;

      // Since event methods can possible be run multiple times for the same event
      // outer node, it is possible that one event can be run multiple times. To
      // prevent needless potentially expensive recalculations, this weak map
      // tracks the references to an event for automatic garbage collection when
      // the reference to the event is lost.
      var eventMap = new WeakMap();
      return {
        props: {
          handleKeyPress: function handleKeyPress(_, event) {
            return _this2.options.keypress(event) || false;
          },
          handleKeyDown: function handleKeyDown(_, event) {
            return _this2.options.keydown(event) || false;
          },
          handleTextInput: function handleTextInput(_, from, to, text) {
            return _this2.options.textInput({
              from: from,
              to: to,
              text: text
            }) || false;
          },
          handleClickOn: function handleClickOn(view, pos, node, nodePos, event, direct) {
            var state = _this2.store.currentState;
            var schema = state.schema,
                doc = state.doc;
            var $pos = doc.resolve(pos); // True when the event has already been handled. In these cases we
            // should **not** run the `clickMark` handler since all that is needed
            // is the `$pos` property to check if a mark is active.

            var handled = eventMap.has(event); // Generate the base state which is passed to the `clickMark` handler
            // and used to create the `click` handler state.

            var baseState = createClickMarkState({
              $pos: $pos,
              handled: handled,
              view: view,
              state: state
            });
            var returnValue = false;

            if (!handled) {
              // The boolean return value for the mark click handler. This is
              // intentionally separate so that both the `clickMark` handlers and
              // the `click` handlers are run for each click. It uses the eventMap
              // to limit the ensure that it is only run once per click since this
              // method is run with the same event for every single node in the
              // `doc` tree.
              returnValue = _this2.options.clickMark(event, baseState) || returnValue;
            } // Create click state to help API consumers inspect whether the event
            // is a relevant click type.


            var clickState = _objectSpread(_objectSpread({}, baseState), {}, {
              pos: pos,
              direct: direct,
              nodeWithPosition: {
                node: node,
                pos: nodePos
              },
              getNode: function getNode(nodeType) {
                var type = isString(nodeType) ? schema.nodes[nodeType] : nodeType;
                !type ? process.env.NODE_ENV !== "production" ? invariant(false, {
                  code: ErrorConstant.EXTENSION,
                  message: 'The node being checked does not exist'
                }) : invariant(false) : void 0;
                return type === node.type ? {
                  node: node,
                  pos: nodePos
                } : undefined;
              }
            }); // Store this event so that marks aren't re-run for identical events.


            eventMap.set(event, true);
            return _this2.options.click(event, clickState) || returnValue;
          },
          handleDOMEvents: {
            focus: function focus(_, event) {
              return _this2.options.focus(event) || false;
            },
            blur: function blur(_, event) {
              return _this2.options.blur(event) || false;
            },
            mousedown: function mousedown(_, event) {
              _this2.startMouseover();

              return _this2.options.mousedown(event) || false;
            },
            mouseup: function mouseup(_, event) {
              _this2.endMouseover();

              return _this2.options.mouseup(event) || false;
            },
            mouseleave: function mouseleave(_, event) {
              _this2.mouseover = false;
              return _this2.options.mouseleave(event) || false;
            },
            mouseenter: function mouseenter(_, event) {
              _this2.mouseover = true;
              return _this2.options.mouseenter(event) || false;
            },
            keyup: function keyup(_, event) {
              return _this2.options.keyup(event) || false;
            },
            mouseout: this.createMouseEventHandler(function (props) {
              return _this2.options.hover(_objectSpread(_objectSpread({}, props), {}, {
                hovering: false
              })) || false;
            }),
            mouseover: this.createMouseEventHandler(function (props) {
              return _this2.options.hover(_objectSpread(_objectSpread({}, props), {}, {
                hovering: true
              })) || false;
            }),
            contextmenu: this.createMouseEventHandler(function (props) {
              return _this2.options.contextmenu(props) || false;
            }),
            scroll: function scroll(_, event) {
              return _this2.options.scroll(event) || false;
            },
            copy: function copy(_, event) {
              return _this2.options.copy(event) || false;
            },
            paste: function paste(_, event) {
              return _this2.options.paste(event) || false;
            }
          }
        }
      };
    }
    /**
     * Check if the user is currently interacting with the editor.
     */

  }, {
    key: "isInteracting",
    value: function isInteracting() {
      return this.mousedown && this.mouseover;
    }
  }, {
    key: "startMouseover",
    value: function startMouseover() {
      var _this3 = this;

      this.mouseover = true;

      if (this.mousedown) {
        return;
      }

      this.mousedown = true;
      this.store.document.documentElement.addEventListener('mouseup', function () {
        _this3.endMouseover();
      }, {
        once: true
      });
    }
  }, {
    key: "endMouseover",
    value: function endMouseover() {
      if (!this.mousedown) {
        return;
      }

      this.mousedown = false;
      this.store.commands.emptyUpdate();
    }
  }]);

  return EventsExtension;
}(PlainExtension), _applyDecoratedDescriptor(_class2.prototype, "isInteracting", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "isInteracting"), _class2.prototype), _class2)) || _class);
/**
 * Create the click handler state for the mark.
 */

function createClickMarkState(props) {
  var handled = props.handled,
      view = props.view,
      $pos = props.$pos,
      state = props.state;
  var clickState = {
    getMark: noop,
    markRanges: [],
    view: view,
    state: state
  };

  if (handled) {
    return clickState;
  }

  var _iterator5 = _createForOfIteratorHelper($pos.marks()),
      _step5;

  try {
    for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
      var type = _step5.value.type;

      var _range2 = getMarkRange($pos, type);

      if (_range2) {
        clickState.markRanges.push(_range2);
      }
    }
  } catch (err) {
    _iterator5.e(err);
  } finally {
    _iterator5.f();
  }

  clickState.getMark = function (markType) {
    var type = isString(markType) ? state.schema.marks[markType] : markType;
    !type ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.EXTENSION,
      message: "The mark ".concat(markType, " being checked does not exist within the editor schema.")
    }) : invariant(false) : void 0;
    return clickState.markRanges.find(function (range) {
      return range.mark.type === type;
    });
  };

  return clickState;
}
/**
 * @deprecated use [[`ClickEventHandler`]] instead.
 */


export { EventsExtension };